import { useState, useEffect } from 'react';
import { ChevronLeft, Save, Truck, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { SignedLink } from '../common/SignedFile';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from '../../hooks/useNavigate';
import { parseTrailers, formatTrailers } from '../../lib/trailers';

interface EditTruckPageProps {
  truckId: string;
}

export function EditTruckPage({ truckId }: EditTruckPageProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Truck Details
  const [unitNumber, setUnitNumber] = useState('');
  const [contractor, setContractor] = useState('');
  const [status, setStatus] = useState('in_service');
  const [ownership, setOwnership] = useState<'company' | 'driver_owned'>('company');
  const [ownerDriverId, setOwnerDriverId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [trailers, setTrailers] = useState('');
  const [assignedDriver, setAssignedDriver] = useState<any>(null);
  const [samsaraEnabled, setSamsaraEnabled] = useState(false);
  const [prepass, setPrepass] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [registrationExpiration, setRegistrationExpiration] = useState('');
  const [dotExpiration, setDotExpiration] = useState('');
  const [ifta2025, setIfta2025] = useState(false);
  const [truckStatus, setTruckStatus] = useState<'ACTIVE' | 'ALERT' | 'POTENCIJALNI RECOVERY' | 'RECOVERY - 100%' | 'COMING BACK' | 'JARDA - AVAILABLE' | 'JARDA - VACATION' | 'SHOP/NA YARDI - nasi kamioni' | 'SHOP OWNERI' | 'VACATION - ALI NISU NA JARDI PARKIRANI' | 'INACTIVE'>('ACTIVE');

  // Basic Info
  const [vin, setVin] = useState('');
  const [make, setMake] = useState('');
  const [year, setYear] = useState('');
  const [fuelType, setFuelType] = useState('');
  const [tireSize, setTireSize] = useState('');

  // Tag
  const [tag, setTag] = useState('');
  const [tagNumber, setTagNumber] = useState('');
  const [tagState, setTagState] = useState('');
  const [tagExpiration, setTagExpiration] = useState('');

  // Annual DOT
  const [dotIssuedDate, setDotIssuedDate] = useState('');
  const [dotState, setDotState] = useState('');
  const [dotExpirationDate, setDotExpirationDate] = useState('');

  // Insurance
  const [insuranceType, setInsuranceType] = useState('');
  const [insuranceCarrier, setInsuranceCarrier] = useState('');
  const [insuranceExpirationDate, setInsuranceExpirationDate] = useState('');

  // IFTA Decals
  const [iftaDecalNumber, setIftaDecalNumber] = useState('');
  const [ifta2024, setIfta2024] = useState('');
  const [ifta2025Decal, setIfta2025Decal] = useState('');
  const [ifta2026Decal, setIfta2026Decal] = useState('');

  // File Uploads
  const [registrationFile, setRegistrationFile] = useState<File | null>(null);
  const [dotFile, setDotFile] = useState<File | null>(null);
  const [registrationFileUrl, setRegistrationFileUrl] = useState('');
  const [dotFileUrl, setDotFileUrl] = useState('');
  const [uploadingFiles, setUploadingFiles] = useState(false);

  // Companies
  const [companies, setCompanies] = useState<Array<{ id: string; name: string; code: string }>>([]);

  useEffect(() => {
    loadTruckData();
    loadCompanies();
  }, [truckId]);

  async function loadCompanies() {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, code')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setCompanies(data || []);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  }

  async function loadTruckData() {
    try {
      const { data, error } = await supabase
        .from('trucks')
        .select('*')
        .eq('id', truckId)
        .single();

      if (error) throw error;

      // Load assigned driver to get driver_type
      const { data: assignment } = await supabase
        .from('equipment_assignments')
        .select('driver_id, drivers(id, first_name, last_name, driver_type)')
        .eq('truck_id', truckId)
        .eq('is_current', true)
        .single();

      if (assignment?.drivers) {
        setAssignedDriver(assignment.drivers);
      }

      // Populate all fields
      setUnitNumber(data.unit_number || '');
      setContractor(data.contractor || '');
      setStatus(data.status || 'in_service');
      setOwnership(data.ownership || 'company');
      setOwnerDriverId(data.owner_driver_id || '');
      setCompanyId(data.company_id || '');
      setTrailers(formatTrailers(data.trailers));
      setSamsaraEnabled(data.samsara_enabled || false);
      setPrepass(data.prepass || '');
      setLicensePlate(data.license_plate || '');
      setRegistrationExpiration(data.registration_expiration || '');
      setDotExpiration(data.dot_expiration || '');
      setIfta2024(data.ifta_2024 || '');
      setIfta2025Decal(data.ifta_2025 || '');
      setIfta2026Decal(data.ifta_2026 || '');
      setTruckStatus(data.truck_status || 'ACTIVE');
      setVin(data.vin || '');
      setMake(data.make || '');
      setYear(data.year?.toString() || '');
      setFuelType(data.fuel_type || '');
      setTireSize(data.tire_size || '');
      setTag(data.tag || '');
      setTagNumber(data.tag_number || '');
      setTagState(data.tag_state || '');
      setTagExpiration(data.tag_expiration || '');
      setDotIssuedDate(data.dot_issued_date || '');
      setDotState(data.dot_state || '');
      setDotExpirationDate(data.dot_expiration_date || '');
      setInsuranceType(data.insurance_type || '');
      setInsuranceCarrier(data.insurance_carrier || '');
      setInsuranceExpirationDate(data.insurance_expiration_date || '');
      setIftaDecalNumber(data.ifta_decal_number || '');
      setRegistrationFileUrl(data.registration_file_url || '');
      setDotFileUrl(data.dot_file_url || '');
    } catch (error) {
      console.error('Error loading truck:', error);
      toast.error('Failed to load truck data');
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Upload files if selected
      let newRegistrationFileUrl = registrationFileUrl;
      let newDotFileUrl = dotFileUrl;

      if (registrationFile || dotFile) {
        setUploadingFiles(true);
        
        // Upload registration file
        if (registrationFile) {
          const fileExt = registrationFile.name.split('.').pop();
          const fileName = `registration_${unitNumber}_${Date.now()}.${fileExt}`;
          const filePath = `truck-documents/${unitNumber}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('company-documents')
            .upload(filePath, registrationFile, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
            .from('company-documents')
            .getPublicUrl(filePath);

          newRegistrationFileUrl = publicUrl;
        }

        // Upload DOT file
        if (dotFile) {
          const fileExt = dotFile.name.split('.').pop();
          const fileName = `dot_${unitNumber}_${Date.now()}.${fileExt}`;
          const filePath = `truck-documents/${unitNumber}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('company-documents')
            .upload(filePath, dotFile, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
            .from('company-documents')
            .getPublicUrl(filePath);

          newDotFileUrl = publicUrl;
        }

        setUploadingFiles(false);
      }

      const { error } = await supabase.from('trucks').update({
        unit_number: unitNumber,
        contractor: contractor,
        status: status,
        ownership: ownership,
        owner_driver_id: ownership === 'driver_owned' ? ownerDriverId || null : null,
        company_id: companyId || null,
        trailers: parseTrailers(trailers),
        samsara_enabled: samsaraEnabled,
        prepass: prepass || null,
        license_plate: licensePlate || null,
        registration_expiration: registrationExpiration || null,
        dot_expiration: dotExpiration || null,
        truck_status: truckStatus,
        vin: vin,
        make: make,
        model: make,
        year: parseInt(year),
        fuel_type: fuelType,
        tire_size: tireSize,
        tag: tag,
        tag_number: tagNumber,
        tag_state: tagState,
        tag_expiration: tagExpiration || null,
        dot_issued_date: dotIssuedDate || null,
        dot_state: dotState || null,
        dot_expiration_date: dotExpirationDate || null,
        insurance_type: insuranceType || null,
        insurance_carrier: insuranceCarrier || null,
        insurance_expiration_date: insuranceExpirationDate || null,
        ifta_decal_number: iftaDecalNumber || null,
        ifta_2024: ifta2024 || null,
        ifta_2025: ifta2025Decal || null,
        ifta_2026: ifta2026Decal || null,
        registration_file_url: newRegistrationFileUrl || null,
        dot_file_url: newDotFileUrl || null,
      }).eq('id', truckId);

      if (error) throw error;

      toast.success('Truck updated successfully!');
      navigate(`/fleet/${truckId}`);
    } catch (error) {
      console.error('Error updating truck:', error);
      toast.error(`Failed to update truck: ${(error as any)?.message || 'unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/fleet')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <Truck className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Edit Truck</h1>
                  <p className="text-sm text-slate-600">Update truck information</p>
                </div>
              </div>
            </div>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Truck Details */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Truck Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Unit Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={unitNumber}
                  onChange={(e) => setUnitNumber(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Company
                </label>
                <select
                  value={companyId}
                  onChange={(e) => setCompanyId(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Company</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name} ({company.code})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Contractor
                </label>
                <input
                  type="text"
                  value={contractor}
                  onChange={(e) => setContractor(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Trailer
                </label>
                <input
                  type="text"
                  value={trailers}
                  onChange={(e) => setTrailers(e.target.value)}
                  placeholder="e.g., TR-101"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  License Plate
                </label>
                <input
                  type="text"
                  value={licensePlate}
                  onChange={(e) => setLicensePlate(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Prepass
                </label>
                <input
                  type="text"
                  value={prepass}
                  onChange={(e) => setPrepass(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Driver Status
                </label>
                {assignedDriver?.driver_type ? (
                  <div className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-slate-50">
                    <span className={`inline-block px-3 py-1 rounded text-sm font-medium ${
                      assignedDriver.driver_type === 'owner_operator' ? 'bg-purple-100 text-purple-700' :
                      assignedDriver.driver_type === 'company_driver' ? 'bg-blue-100 text-blue-700' :
                      assignedDriver.driver_type === 'rent' ? 'bg-orange-100 text-orange-700' :
                      assignedDriver.driver_type === 'buy' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {assignedDriver.driver_type === 'owner_operator' ? 'OWNER' :
                       assignedDriver.driver_type === 'company_driver' ? 'COMPANY' :
                       assignedDriver.driver_type === 'rent' ? 'RENT' :
                       assignedDriver.driver_type === 'buy' ? 'BUYING' :
                       assignedDriver.driver_type.toUpperCase()}
                    </span>
                    <span className="ml-2 text-xs text-slate-500">(Auto-populated from driver)</span>
                  </div>
                ) : (
                  <div className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-400">
                    No driver assigned
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Truck Status <span className="text-red-500">*</span>
                </label>
                <select
                  value={truckStatus}
                  onChange={(e) => setTruckStatus(e.target.value as any)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="ACTIVE">Active</option>
                  <option value="ALERT">Alert - FC, ne voze, shop na putu, medical itd.</option>
                  <option value="POTENCIJALNI RECOVERY">Potencijalni Recovery</option>
                  <option value="RECOVERY - 100%">Recovery - 100%</option>
                  <option value="COMING BACK">Coming Back</option>
                  <option value="JARDA - AVAILABLE">Jarda - Available</option>
                  <option value="JARDA - VACATION">Jarda - Vacation</option>
                  <option value="SHOP/NA YARDI - nasi kamioni">Shop/Na Yardi - nasi kamioni</option>
                  <option value="SHOP OWNERI">Shop Owneri</option>
                  <option value="VACATION - ALI NISU NA JARDI PARKIRANI">Vacation - ali nisu na jardi parkirani</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Registration Expiration
                </label>
                <input
                  type="date"
                  value={registrationExpiration}
                  onChange={(e) => setRegistrationExpiration(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  DOT Expiration
                </label>
                <input
                  type="date"
                  value={dotExpiration}
                  onChange={(e) => setDotExpiration(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={samsaraEnabled}
                    onChange={(e) => setSamsaraEnabled(e.target.checked)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 rounded"
                  />
                  <span className="text-sm font-medium text-slate-700">Samsara Enabled</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ifta2025}
                    onChange={(e) => setIfta2025(e.target.checked)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 rounded"
                  />
                  <span className="text-sm font-medium text-slate-700">IFTA 2025</span>
                </label>
              </div>
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Ownership <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="company"
                      checked={ownership === 'company'}
                      onChange={(e) => setOwnership(e.target.value as 'company')}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-blue-200 bg-blue-50 text-blue-700 font-medium">
                      🏢 Company Owned
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="driver_owned"
                      checked={ownership === 'driver_owned'}
                      onChange={(e) => setOwnership(e.target.value as 'driver_owned')}
                      className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-purple-200 bg-purple-50 text-purple-700 font-medium">
                      👤 Driver Owned
                    </span>
                  </label>
                </div>
                {ownership === 'driver_owned' && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Owner Driver ID
                    </label>
                    <input
                      type="text"
                      value={ownerDriverId}
                      onChange={(e) => setOwnerDriverId(e.target.value)}
                      placeholder="Enter driver ID who owns this truck"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                    <p className="mt-1 text-xs text-slate-500">Optional: Link this truck to the owner operator who owns it</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Basic Info</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  VIN <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={vin}
                  onChange={(e) => setVin(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Make <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={make}
                  onChange={(e) => setMake(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Year <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fuel Type</label>
                <input
                  type="text"
                  value={fuelType}
                  onChange={(e) => setFuelType(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tire Size</label>
                <input
                  type="text"
                  value={tireSize}
                  onChange={(e) => setTireSize(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Tag Information */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Tag Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tag</label>
                <input
                  type="text"
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tag Number</label>
                <input
                  type="text"
                  value={tagNumber}
                  onChange={(e) => setTagNumber(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tag State</label>
                <input
                  type="text"
                  value={tagState}
                  onChange={(e) => setTagState(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  maxLength={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tag Expiration</label>
                <input
                  type="date"
                  value={tagExpiration}
                  onChange={(e) => setTagExpiration(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Annual DOT */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Annual DOT</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Issued Date</label>
                <input
                  type="date"
                  value={dotIssuedDate}
                  onChange={(e) => setDotIssuedDate(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
                <input
                  type="text"
                  value={dotState}
                  onChange={(e) => setDotState(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  maxLength={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Expiration Date</label>
                <input
                  type="date"
                  value={dotExpirationDate}
                  onChange={(e) => setDotExpirationDate(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Insurance */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Insurance</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                <input
                  type="text"
                  value={insuranceType}
                  onChange={(e) => setInsuranceType(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Carrier</label>
                <input
                  type="text"
                  value={insuranceCarrier}
                  onChange={(e) => setInsuranceCarrier(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Expiration Date</label>
                <input
                  type="date"
                  value={insuranceExpirationDate}
                  onChange={(e) => setInsuranceExpirationDate(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* IFTA Decals */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">IFTA Decals</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">IFTA 2024 Decal Number</label>
                <input
                  type="text"
                  value={ifta2024}
                  onChange={(e) => setIfta2024(e.target.value)}
                  placeholder="e.g., 004561 Apollo 2024"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">IFTA 2025 Decal Number</label>
                <input
                  type="text"
                  value={ifta2025Decal}
                  onChange={(e) => setIfta2025Decal(e.target.value)}
                  placeholder="e.g., 004583 Apollo 2025"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  <span className="font-semibold">IFTA 2026 Decal Number</span>
                  <span className="ml-2 text-xs text-blue-600">(Current Year)</span>
                </label>
                <input
                  type="text"
                  value={ifta2026Decal}
                  onChange={(e) => setIfta2026Decal(e.target.value)}
                  placeholder="e.g., 004590 Apollo 2026"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Document Uploads */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Document Uploads</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Registration File */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Registration File</label>
                <div className="space-y-2">
                  {registrationFileUrl && !registrationFile && (
                    <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm text-green-700">File uploaded</span>
                      <SignedLink
                        stored={registrationFileUrl}
                        className="text-sm text-blue-600 hover:text-blue-700 underline"
                      >
                        View
                      </SignedLink>
                      <button
                        type="button"
                        onClick={() => setRegistrationFileUrl('')}
                        className="ml-auto p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                        title="Remove file"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setRegistrationFile(file);
                      }
                    }}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                  {registrationFile && (
                    <div className="text-sm text-slate-600">
                      Selected: {registrationFile.name}
                    </div>
                  )}
                  <p className="text-xs text-slate-500">Accepts PDF and images (JPG, PNG)</p>
                </div>
              </div>

              {/* DOT File */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">DOT File</label>
                <div className="space-y-2">
                  {dotFileUrl && !dotFile && (
                    <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm text-green-700">File uploaded</span>
                      <SignedLink
                        stored={dotFileUrl}
                        className="text-sm text-blue-600 hover:text-blue-700 underline"
                      >
                        View
                      </SignedLink>
                      <button
                        type="button"
                        onClick={() => setDotFileUrl('')}
                        className="ml-auto p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                        title="Remove file"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setDotFile(file);
                      }
                    }}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                  {dotFile && (
                    <div className="text-sm text-slate-600">
                      Selected: {dotFile.name}
                    </div>
                  )}
                  <p className="text-xs text-slate-500">Accepts PDF and images (JPG, PNG)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate(`/fleet/${truckId}`)}
              className="px-6 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
